"use client"
import { createContext, useContext, useState, useEffect, useLayoutEffect, type ReactNode } from "react"
import Cookies from "js-cookie"
import { usePathname } from "next/navigation"
import { useRole } from "@/contexts/role-context"
import settingsService from "@/services/settings-service"
import {
    buildUserScopeHash,
    COOKIE_KEYS,
    resolveThemeBootstrapCookieKeyForUser,
    resolveThemeBootstrapCookieKeyFromScopeHash,
    resolveActiveUserScopeCookieKey,
    resolveBootstrapScopeFromPathname,
    resolveBootstrapScopeFromRole,
    resolveThemeBootstrapCookieKey,
} from "@/lib/cookie-keys"

export type ThemeType = "light" | "dark" | "system"
export type LanguageType = "ro" | "en" | "de"
export type AccentColor = "emerald" | "blue" | "violet" | "amber" | "rose"

export interface UserPreferences {
    theme: ThemeType
    language: LanguageType
    accentColor: AccentColor
    timezone: string
    currency: string
    emailNotifications: boolean
    pushNotifications: boolean
    smsNotifications: boolean
    newOrdersNotification: boolean
    statusUpdatesNotification: boolean
    weeklyReportNotification: boolean
    twoFactorEnabled: boolean
}

// Culori disponibile cu mapping-ul la valori hex pentru CSS
export const COLOR_THEMES = {
    emerald: {
        name: "Emerald (Standard)",
        primary: "#059669", // emerald-600
        light: "#d1fae5", // emerald-100
        dark: "#064e3b", // emerald-900
    },
    blue: {
        name: "Blue Royal",
        primary: "#2563eb", // blue-600
        light: "#dbeafe", // blue-100
        dark: "#1e3a8a", // blue-900
    },
    violet: {
        name: "Violet Premium",
        primary: "#7c3aed", // violet-600
        light: "#ede9fe", // violet-100
        dark: "#4c1d95", // violet-900
    },
    amber: {
        name: "Amber Industrial",
        primary: "#f59e0b", // amber-500
        light: "#fef3c7", // amber-100
        dark: "#92400e", // amber-900
    },
    rose: {
        name: "Rose Alert",
        primary: "#e11d48", // rose-600
        light: "#ffe4e6", // rose-100
        dark: "#4c0519", // rose-900
    },
}

interface PreferencesContextType {
    preferences: UserPreferences
    updatePreferences: (prefs: Partial<UserPreferences>) => void
    applyColorTheme: (color: AccentColor) => void
    resetPreferences: () => void
    isLoading: boolean
}

const PREFERENCES_COOKIE_PREFIX = COOKIE_KEYS.preferencesPrefix
const COOKIE_EXPIRES_DAYS = 365

export const DEFAULT_PREFERENCES: UserPreferences = {
    theme: "system",
    language: "ro",
    accentColor: "emerald",
    timezone: "Europe/Bucharest",
    currency: "EUR",
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    newOrdersNotification: true,
    statusUpdatesNotification: true,
    weeklyReportNotification: false,
    twoFactorEnabled: false,
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined)

const AUTH_SEGMENTS = new Set([
    "login",
    "register",
    "forgot-password",
    "reset-password",
    "verify-email",
    "admin-setup",
])

function isLightLockedRoute(pathname: string): boolean {
    const cleaned = pathname.split("?")[0].split("#")[0]
    const segments = cleaned.split("/").filter(Boolean)

    // /ro, /en, /de -> landing page
    if (segments.length === 1) return true

    // /ro/login, /ro/register, ... -> auth pages
    return segments.length >= 2 && AUTH_SEGMENTS.has(segments[1])
}

const getDefaultPreferences = (language?: LanguageType): UserPreferences => ({
    ...DEFAULT_PREFERENCES,
    language: language ?? DEFAULT_PREFERENCES.language,
})

const getScopedPreferencesCookieKey = (userId?: number | null): string => {
    if (typeof userId === "number" && Number.isFinite(userId)) {
        return `${PREFERENCES_COOKIE_PREFIX}_u_${buildUserScopeHash(userId)}`
    }

    return `${PREFERENCES_COOKIE_PREFIX}_guest`
}

const getAllThemeBootstrapCookieKeys = (): string[] => {
    return Object.values(COOKIE_KEYS.themeBootstrapByScope)
}

const pruneInactiveThemeBootstrapCookies = (activeCookieKey: string) => {
    const allThemeBootstrapCookies = getAllThemeBootstrapCookieKeys()
    const inactiveThemeBootstrapCookies = allThemeBootstrapCookies.filter((cookieKey) => cookieKey !== activeCookieKey)
    inactiveThemeBootstrapCookies.forEach((cookieKey) => {
        Cookies.remove(cookieKey)
    })
}

const readBootstrapVisualPreferences = (
    cookieKey: string,
    initialLanguage?: LanguageType,
): Pick<UserPreferences, "theme" | "accentColor"> | null => {
    try {
        const stored = Cookies.get(cookieKey)
        if (!stored) return null

        const parsed = JSON.parse(stored)
        const defaults = getDefaultPreferences(initialLanguage)

        const theme: ThemeType =
            parsed.theme === "light" || parsed.theme === "dark" || parsed.theme === "system"
                ? parsed.theme
                : defaults.theme

        const accentColor: AccentColor =
            parsed.accentColor === "emerald" || parsed.accentColor === "blue" || parsed.accentColor === "violet" || parsed.accentColor === "amber" || parsed.accentColor === "rose"
                ? parsed.accentColor
                : defaults.accentColor

        return { theme, accentColor }
    } catch {
        return null
    }
}

const readPreferencesFromCookie = (cookieKey: string, initialLanguage?: LanguageType): UserPreferences | null => {
    try {
        const stored = Cookies.get(cookieKey)
        if (!stored) return null
        const parsed = JSON.parse(stored)
        return { ...getDefaultPreferences(initialLanguage), ...parsed }
    } catch {
        return null
    }
}

// Module-level so it can be used before component mounts (e.g. in persistBootstrap).
const resolveEffectiveTheme = (theme: ThemeType): "light" | "dark" => {
    if (theme === "dark") return "dark"
    if (theme === "light") return "light"
    if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark"
    }
    return "light"
}

export function PreferencesProvider({ children, initialLanguage }: { children: ReactNode; initialLanguage?: LanguageType }) {
    const pathname = usePathname()
    const { user, isLoading: isAuthLoading } = useRole()
    const isLightLocked = isLightLockedRoute(pathname || "")
    const pathScope = resolveBootstrapScopeFromPathname(pathname)
    const roleScope = resolveBootstrapScopeFromRole(user?.role)
    const bootstrapScope = roleScope === "global" ? pathScope : roleScope
    const activeUserScopeHash = Cookies.get(resolveActiveUserScopeCookieKey(bootstrapScope))
    // On public/auth routes ignore user-scoped cookies completely to prevent bleed from logged-in sessions.
    const visualBootstrapCookieKey = isLightLocked
        ? null
        : typeof user?.id === "number" && Number.isFinite(user.id)
            ? resolveThemeBootstrapCookieKeyForUser(user.id)
            : activeUserScopeHash
                ? resolveThemeBootstrapCookieKeyFromScopeHash(activeUserScopeHash)
                : resolveThemeBootstrapCookieKey(bootstrapScope)

    const [preferences, setPreferences] = useState<UserPreferences>(() => {
        const defaults = getDefaultPreferences(initialLanguage)
        if (isLightLocked || !visualBootstrapCookieKey) return defaults

        const bootstrapVisual = readBootstrapVisualPreferences(visualBootstrapCookieKey, initialLanguage)
        return bootstrapVisual
            ? { ...defaults, theme: bootstrapVisual.theme, accentColor: bootstrapVisual.accentColor }
            : defaults
    })
    const [isLoading, setIsLoading] = useState(true)

    const preferencesCookieKey = getScopedPreferencesCookieKey(user?.id ?? null)

    const applyThemeClass = (theme: ThemeType) => {
        const root = document.documentElement
        const resolved = isLightLocked ? "light" : resolveEffectiveTheme(theme)
        root.classList.remove("light", "dark")
        root.classList.add(resolved)
        root.style.colorScheme = resolved
    }

    const persistPreferences = (nextPreferences: UserPreferences) => {
        if (isLightLocked || !visualBootstrapCookieKey) return

        try {
            Cookies.set(preferencesCookieKey, JSON.stringify(nextPreferences), {
                expires: COOKIE_EXPIRES_DAYS,
                sameSite: "lax",
                secure: typeof window !== "undefined" ? window.location.protocol === "https:" : false,
            })

            // Always store resolved light/dark so the server can bootstrap the correct class without system-mode ambiguity.
            const resolvedBootstrapTheme = resolveEffectiveTheme(nextPreferences.theme)
            Cookies.set(visualBootstrapCookieKey, JSON.stringify({ theme: resolvedBootstrapTheme, accentColor: nextPreferences.accentColor }), {
                expires: COOKIE_EXPIRES_DAYS,
                sameSite: "lax",
                secure: typeof window !== "undefined" ? window.location.protocol === "https:" : false,
            })

            // Keep only the active scope cookie to avoid stale duplicates across roles.
            pruneInactiveThemeBootstrapCookies(visualBootstrapCookieKey)
        } catch (error) {
            console.error("Failed to save preferences to cookie:", error)
        }
    }

    // --- LOAD PREFERENCES FROM COOKIE ---
    useLayoutEffect(() => {
        let isCancelled = false

        const applyResolvedPreferences = (nextPreferences: UserPreferences) => {
            if (isCancelled) return
            setPreferences(nextPreferences)
            applyThemeClass(nextPreferences.theme)
            applyColorTheme(nextPreferences.accentColor)
        }

        if (isLightLocked) {
            const defaults = getDefaultPreferences(initialLanguage)
            applyResolvedPreferences(defaults)
            setIsLoading(false)
            return () => { isCancelled = true }
        }

        if (isAuthLoading) {
            const defaults = getDefaultPreferences(initialLanguage)
            const bootstrapVisual = visualBootstrapCookieKey
                ? readBootstrapVisualPreferences(visualBootstrapCookieKey, initialLanguage)
                : null

            // Only touch the DOM when a real cookie exists; otherwise keep the SSR-rendered class.
            if (bootstrapVisual) {
                applyResolvedPreferences({ ...defaults, theme: bootstrapVisual.theme, accentColor: bootstrapVisual.accentColor })
            }
            setIsLoading(true)
            return () => { isCancelled = true }
        }

        const defaults = getDefaultPreferences(initialLanguage)

        const scopedPreferences = readPreferencesFromCookie(preferencesCookieKey, initialLanguage)
        const loadedPreferences = scopedPreferences

        if (loadedPreferences) {
            applyResolvedPreferences(loadedPreferences)
            if (visualBootstrapCookieKey) pruneInactiveThemeBootstrapCookies(visualBootstrapCookieKey)

            setIsLoading(false)
            return () => {
                isCancelled = true
            }
        }

        const bootstrapVisual = visualBootstrapCookieKey
            ? readBootstrapVisualPreferences(visualBootstrapCookieKey, initialLanguage)
            : null

        // Only touch the DOM from bootstrap when it exists; otherwise the SSR class is already correct and
        // hydrateFromServer below will apply the right theme once data arrives.
        if (bootstrapVisual) {
            applyResolvedPreferences({ ...defaults, theme: bootstrapVisual.theme, accentColor: bootstrapVisual.accentColor })
        }
        if (visualBootstrapCookieKey) pruneInactiveThemeBootstrapCookies(visualBootstrapCookieKey)

        if (!user?.id) {
            setIsLoading(false)
            return () => {
                isCancelled = true
            }
        }

        const hydrateFromServer = async () => {
            try {
                const response = await settingsService.getUserPreferences()
                if (isCancelled || !response.success || !response.data) return

                const hydratedPreferences: UserPreferences = {
                    ...defaults,
                    ...response.data,
                }

                applyResolvedPreferences(hydratedPreferences)
                persistPreferences(hydratedPreferences)
            } catch (error) {
                if (!isCancelled) {
                    console.error("Failed to hydrate user preferences:", error)
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false)
                }
            }
        }

        void hydrateFromServer()

        return () => {
            isCancelled = true
        }
    }, [initialLanguage, isLightLocked, isAuthLoading, preferencesCookieKey, user?.id, visualBootstrapCookieKey])

    // --- APPLY COLOR THEME TO DOM ---
    const applyColorTheme = (color: AccentColor) => {
        const colorTheme = COLOR_THEMES[color]
        const root = document.documentElement

        // Actualizăm variabilele CSS pentru culoare primară
        root.style.setProperty("--primary", colorTheme.primary)
        root.style.setProperty("--primary-foreground", "oklch(0.99 0.005 160)")

        // Actualizăm de asemenea și colorul sidebar
        root.style.setProperty("--sidebar-primary", colorTheme.primary)

        // Focus/selection ring trebuie să urmeze accentul selectat
        root.style.setProperty("--ring", colorTheme.primary)
        root.style.setProperty("--sidebar-ring", colorTheme.primary)

        // Update chart colors based on primary color
        root.style.setProperty("--chart-1", colorTheme.primary)
    }

    // --- UPDATE PREFERENCES ---
    const updatePreferences = (prefs: Partial<UserPreferences>) => {
        setPreferences((current) => {
            const basePreferences = isLoading ? (readPreferencesFromCookie(preferencesCookieKey, initialLanguage) ?? current) : current
            const updated = { ...basePreferences, ...prefs }

            persistPreferences(updated)

            if (prefs.accentColor && prefs.accentColor !== basePreferences.accentColor) {
                applyColorTheme(prefs.accentColor)
            }

            if (prefs.theme && prefs.theme !== basePreferences.theme) {
                applyThemeClass(prefs.theme)
            }

            return updated
        })
    }

    // --- RESET TO DEFAULTS ---
    const resetPreferences = () => {
        const defaults = getDefaultPreferences(initialLanguage)
        setPreferences(defaults)
        persistPreferences(defaults)
        applyColorTheme(defaults.accentColor)
        applyThemeClass(defaults.theme)
    }

    // --- APPLY INITIAL COLOR ON MOUNT ---
    useEffect(() => {
        if (!isLoading) {
            applyColorTheme(preferences.accentColor)
        }
    }, [isLoading, preferences.accentColor])

    useEffect(() => {
        if (isLoading) return

        applyThemeClass(preferences.theme)

        if (preferences.theme !== "system") return

        const media = window.matchMedia("(prefers-color-scheme: dark)")
        const handleChange = () => {
            applyThemeClass("system")
            // Re-persist resolved theme so server bootstrap stays in sync with OS change.
            if (visualBootstrapCookieKey) {
                const secure = window.location.protocol === "https:"
                const resolved = media.matches ? "dark" : "light"
                const currentBootstrap = readBootstrapVisualPreferences(visualBootstrapCookieKey)
                Cookies.set(visualBootstrapCookieKey, JSON.stringify({ theme: resolved, accentColor: currentBootstrap?.accentColor ?? preferences.accentColor }), {
                    expires: COOKIE_EXPIRES_DAYS, sameSite: "lax", secure,
                })
            }
        }

        media.addEventListener("change", handleChange)
        return () => media.removeEventListener("change", handleChange)
    }, [isLoading, preferences.theme, isLightLocked])

    return (
        <PreferencesContext.Provider
            value={{
                preferences,
                updatePreferences,
                applyColorTheme,
                resetPreferences,
                isLoading,
            }}
        >
            {children}
        </PreferencesContext.Provider>
    )
}

export function usePreferences() {
    const context = useContext(PreferencesContext)
    if (context === undefined) {
        throw new Error("usePreferences must be used within PreferencesProvider")
    }
    return context
}
